import type { Campaign, CampaignKind, CampaignResults, FilterKey, GroupFilters, MarketingList } from './api'

/** What each filter is called, and its choices in words. */
export const FILTERS: Record<
  Exclude<FilterKey, 'tag'>,
  { label: string; any: string; values: Record<string, string> }
> = {
  who: {
    label: 'Who in each workspace',
    any: 'Workspace owners',
    values: { owners: 'Workspace owners', everyone: 'Everyone on the team' },
  },
  plan: { label: 'Plan', any: 'Any plan', values: {} },
  billing: {
    label: 'Billing',
    any: 'Any',
    values: {
      paying: 'Paying',
      complimentary: 'Complimentary plan',
      payment_issue: 'Payment failing',
      ending: 'Plan set to end',
      never_paid: 'Never paid',
      lapsed: 'Paid before, not now',
    },
  },
  signed_up: {
    label: 'Signed up',
    any: 'Any time',
    values: {
      '7': 'In the last 7 days',
      '30': 'In the last 30 days',
      '90': 'In the last 90 days',
      '365': 'In the last year',
    },
  },
  activity: {
    label: 'Last used Arva SEO',
    any: 'Any time',
    values: {
      active_7: 'In the last 7 days',
      active_30: 'In the last 30 days',
      inactive_30: 'Not in the last 30 days',
      inactive_90: 'Not in the last 90 days',
      never: 'Never',
    },
  },
  segment: {
    label: 'Segment',
    any: 'Any',
    values: {
      paying: 'Paying',
      free: 'Free',
      new: 'New this week',
      risk: 'At risk',
      ending: 'Plan set to end',
      closing: 'Unused Free, closing soon',
    },
  },
  market: {
    label: 'Where they are',
    any: 'Anywhere',
    values: {
      tier1: 'US, UK, Canada, Australia, Ireland, New Zealand',
      hubs: 'Philippines, South Africa, Singapore, Malaysia',
      other: 'Elsewhere',
    },
  },
  channel: {
    label: 'Signed up from',
    any: 'Anywhere',
    values: {
      google: 'Google Ads',
      meta: 'Meta ads',
      tagged: 'Another campaign link',
      untagged: 'No campaign',
    },
  },
  wordpress: {
    label: 'WordPress',
    any: 'Either',
    values: { connected: 'Connected', not_connected: 'Not connected' },
  },
  google: {
    label: 'Google',
    any: 'Either',
    values: { connected: 'Connected', not_connected: 'Not connected' },
  },
  ai: { label: 'AI tool', any: 'Either', values: { connected: 'Connected', not_connected: 'Not connected' } },
}

export const FILTER_ORDER: Exclude<FilterKey, 'tag'>[] = [
  'who',
  'plan',
  'billing',
  'segment',
  'signed_up',
  'activity',
  'market',
  'channel',
  'wordpress',
  'google',
  'ai',
]

/** The choices of one filter, in words (plans by their names). */
export function choices(key: Exclude<FilterKey, 'tag'>, meta: MarketingList['meta']): [string, string][] {
  if (key === 'plan') return meta.plans.map((p) => [p.key, p.name])
  return (meta.filters[key] ?? Object.keys(FILTERS[key].values)).map((v) => [v, FILTERS[key].values[v] ?? v])
}

/** A group's filters in a few words each: "Plan: Starter · Signed up: In the last 30 days". */
export function describeFilters(
  filters: GroupFilters,
  meta: MarketingList['meta'],
  kind: CampaignKind = 'marketing',
): string {
  const parts = FILTER_ORDER.filter((k) => k !== 'who' && filters[k]).map((k) => {
    const value = filters[k] ?? ''
    const label =
      k === 'plan'
        ? (meta.plans.find((p) => p.key === value)?.name ?? value)
        : (FILTERS[k].values[value] ?? value)
    return `${FILTERS[k].label}: ${label}`
  })
  if (filters.tag) parts.push(`Tag: ${filters.tag}`)
  const opted = kind === 'service' ? '' : ' who opted in'
  const who = filters.who === 'everyone' ? `Everyone${opted}` : `Owners${opted}`
  return parts.length === 0 ? `${who}, every workspace` : `${who} · ${parts.join(' · ')}`
}

/** The filters as the API keeps them: only the ones with a value. */
export function cleanFilters(filters: GroupFilters): GroupFilters {
  return Object.fromEntries(
    Object.entries(filters).filter(([, v]) => typeof v === 'string' && v.trim() !== ''),
  )
}

export const statusChip: Record<Campaign['status'], { label: string; tone: string }> = {
  draft: { label: 'Draft', tone: '' },
  sending: { label: 'Sending', tone: 'warn' },
  sent: { label: 'Sent', tone: 'ok' },
}

/** The chip for a campaign: Scheduled for a draft with a time. */
export function chipFor(c: Pick<Campaign, 'status' | 'scheduled_at'>): { label: string; tone: string } {
  if (c.status === 'draft' && c.scheduled_at) return { label: 'Scheduled', tone: 'lime' }
  return statusChip[c.status]
}

/** A share as a whole percentage, such as "42%" (0% when there's nothing to divide by). */
export function percent(part: number, whole: number): string {
  return `${whole > 0 ? Math.round((part / whole) * 100) : 0}%`
}

/** US cents as dollars, such as "$19" or "$1,234.50". */
export function dollars(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100)
}

/** Why a service notice shows "Not tracked" where opens and clicks would be. */
export const NOT_TRACKED =
  'Service notices go to everyone and aren’t tracked, so opens and clicks aren’t counted. Marketing emails count them.'

/** Opened / clicked as shares of the emails Amazon SES tracked, or null when there's nothing to show. */
export function openedClicked(
  r: Pick<CampaignResults, 'counts_opens' | 'tracked' | 'opened' | 'clicked'>,
): string | null {
  const whole = r.tracked
  if (!r.counts_opens || whole === 0) return null
  return `${percent(r.opened ?? 0, whole)} / ${percent(r.clicked ?? 0, whole)}`
}
